// Instrumentations
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";

// Open Telemetry
//import { ZoneContextManager } from "@opentelemetry/context-zone";
import { WebTracerProvider, StackContextManager } from "@opentelemetry/sdk-trace-web";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { trace, context } from "@opentelemetry/api";
import { Resource } from "@opentelemetry/resources";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";

// Faro
import {
  ConsoleTransport,
  ConsoleInstrumentation,
  ViewInstrumentation,
  ErrorsInstrumentation,
  SessionInstrumentation,
  WebVitalsInstrumentation,
  initializeFaro,
  Instrumentation,
  BaseTransport,
  Faro,
  browserMeta,
  pageMeta,
  createSession
} from "@grafana/faro-web-sdk";
import { defaultBatchingConfig } from "@grafana/faro-core";
import { FaroSessionSpanProcessor, FaroTraceExporter } from "./web-tracing";

// RUM
import TraceLogTransport from "./TraceLogTransport";
import perfInit from "./performanceInstrumentation";
// Types
import type { InstrumentationOption } from "@opentelemetry/instrumentation";
import type { configOptions, instrumentationConfig } from "./types";
import { RumFetchTransport } from "./RumFetchTransport";
import { timeZoneCountryAndCodeMap } from "./timeZoneCountryAndCodeMap";
import { DEFAULT_CONFIG } from "./config";

const DEFAULT_INST_CONFIG: instrumentationConfig = {
  documentload: true,
  xhr: true,
  fetch: true,
  interactions: true,
  webvitals: true,
  console: true,
  errors: true
};

export const otelInstrumentationInit = (config: configOptions, faro: Faro, COLLECTOR_URL: string): void => {
  const instrumentations: InstrumentationOption[] = [];
  const ignoreUrls = config.ignoreUrls ? config.ignoreUrls : [];
  const propagateTraceHeaderCorsUrls = config.propagateTraceHeaderCorsUrls ? config.propagateTraceHeaderCorsUrls : [];
  let instrumentationsConfig = DEFAULT_INST_CONFIG;
  ignoreUrls.push(COLLECTOR_URL);
  if (config.instrumentations) {
    instrumentationsConfig = { ...instrumentationsConfig, ...config.instrumentations };
  }
  const { documentload, xhr, fetch, interactions } = { ...instrumentationsConfig };
  if (xhr) {
    instrumentations.push(
      new XMLHttpRequestInstrumentation({
        ignoreUrls,
        propagateTraceHeaderCorsUrls
      })
    );
  }
  if (fetch) {
    instrumentations.push(
      new FetchInstrumentation({
        ignoreUrls,
        propagateTraceHeaderCorsUrls
      })
    );
  }
  if (documentload) {
    instrumentations.push(new DocumentLoadInstrumentation());
  }
  if (interactions) {
    if (typeof interactions === "object" && "eventNames" in interactions) {
      instrumentations.push(new UserInteractionInstrumentation({ eventNames: interactions.eventNames }));
    } else {
      instrumentations.push(new UserInteractionInstrumentation());
    }
  }
  registerInstrumentations({
    instrumentations: instrumentations
  });
};

// RUM init
// functionalities :
// initialise faro
// add transports
// add instrumentation
export const RumInit = (config: configOptions): Faro => {
  const transports: BaseTransport[] = [];
  const {
    app = { name: "rum-web-sdk", version: "1.0.0", environment: "prod" },
    dedupe = false,
    debug = false,
    traceLog = false,
    sso_user = true,
    isolate = false,
    transportBufferSize = 30,
    rateLimitBackoffMs = 5000,
    useSendBeacon = false,
    metas = [],
    spanLimitInTrace = 10,
    scheduledDelayInTrace = 5000
  } = config;
  if (debug) {
    transports.push(new ConsoleTransport());
  }
  const COLLECTOR_URL = config.collectorUrl || "https://argus.maersk.io/collect";
  // if (app.environment === "dev" || app.environment === "development") {
  //   COLLECTOR_URL = config.collectorUrl || "https://argus.pensieve.maersk-digital.dev/collect";
  // }
  const ratio = config.sessionSampler?.ratio || 1;
  if (Math.random() < ratio) {
    transports.push(
      new RumFetchTransport({
        url: COLLECTOR_URL,
        apiKey: config.apiKey,
        useSendBeacon: useSendBeacon,
        bufferSize: transportBufferSize,
        defaultRateLimitBackoffMs: rateLimitBackoffMs,
        notifyOnBufferFullLimit: config.notifyOnBufferFullLimit
      })
    );
  }
  const getFaroInstrumentations = (config: configOptions): Instrumentation[] => {
    let instrumentationsDefault = DEFAULT_INST_CONFIG;
    if (config.instrumentations) {
      instrumentationsDefault = { ...instrumentationsDefault, ...config.instrumentations };
    }
    const { errors, webvitals, console } = { ...instrumentationsDefault };
    const instrumentations: Instrumentation[] = [];
    if (errors) {
      instrumentations.push(new ErrorsInstrumentation());
    }
    if (webvitals) {
      instrumentations.push(new WebVitalsInstrumentation());
    }
    if (console) {
      if (typeof console === "object" && "disabledLevels" in console) {
        instrumentations.push(new ConsoleInstrumentation({ disabledLevels: console.disabledLevels }));
      } else {
        instrumentations.push(new ConsoleInstrumentation());
      }
    }
    return instrumentations;
  };
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const country = timeZoneCountryAndCodeMap[timezone];
  const { countryName = "N/A", countryCode = "N/A", region = "N/A" } = country || {};
  const defaultSessionAttributes = {
    timezone: timezone,
    country: countryName,
    countryCode: countryCode,
    region: region,
    rum_verison: "2.4.0"
  };

  const faro = initializeFaro({
    app: app,
    dedupe: dedupe,
    isolate: isolate,
    beforeSend: config.beforeSend ?? undefined,
    ignoreErrors: config.ignoreErrors ?? undefined,
    batching: {
      ...defaultBatchingConfig,
      ...config.batching
    },
    instrumentations: [...getFaroInstrumentations(config), new SessionInstrumentation(), new ViewInstrumentation()],
    transports: transports,
    metas: [browserMeta, pageMeta, ...metas],
    sessionTracking: {
      enabled: config.sessionTracking?.enabled ?? DEFAULT_CONFIG.sessionTracking?.enabled,
      persistent: config.sessionTracking?.persistent ?? DEFAULT_CONFIG.sessionTracking?.persistent,
      maxSessionPersistenceTime:
        config.sessionTracking?.maxSessionPersistenceTime ?? DEFAULT_CONFIG.sessionTracking?.maxSessionPersistenceTime,
      session: createSession({ ...defaultSessionAttributes }),
      onSessionChange: config.sessionTracking?.onSessionChange ?? undefined
    }
  });
  if (traceLog) {
    faro.transports.add(...transports, new TraceLogTransport(faro, config.ignoreUrls ? config.ignoreUrls : []));
  }

  // sso user
  if (sso_user) {
    let id = "";
    const sessionData = window.sessionStorage || "";
    if (sessionData) {
      const regex = /"homeAccountId":"[-\w.]*"/gm;
      for (const k in sessionData) {
        if (sessionData[k]) {
          const sessionValue = sessionData[k].toString().match(regex);
          if (sessionValue) {
            const value = sessionValue[0].split(":")[1] || "";
            id = value.replace(/['"]+/g, "");
            break;
          }
        }
      }
    }
    faro.api.setUser({ id });
  }
  const { name, version } = app;
  const resource = Resource.default().merge(
    new Resource({
      [SemanticResourceAttributes.SERVICE_NAME]: name,
      [SemanticResourceAttributes.SERVICE_VERSION]: version
    })
  );
  if (config.performanceInstrument) {
    perfInit(faro, config.performanceInstrument.ignoreEvents || []);
  }
  const provider = new WebTracerProvider({ resource });
  provider.addSpanProcessor(
    new FaroSessionSpanProcessor(
      new BatchSpanProcessor(new FaroTraceExporter({ api: faro.api }), {
        scheduledDelayMillis: scheduledDelayInTrace,
        maxExportBatchSize: spanLimitInTrace
      }),
      faro.metas
    )
  );
  provider.register({
    propagator: new W3CTraceContextPropagator(),
    contextManager: new StackContextManager()
  });
  otelInstrumentationInit(config, faro, COLLECTOR_URL);
  faro.api.initOTEL(trace, context);
  return faro;
};
